// Deployments of a repository (/v1/repositories/{id}/deployments). A
// deployment fixes one LiveReportArtefact at one commit and reaches its
// audience through channels: the live URL, a signed link in a report mail,
// and a webhook. A build of it for one report period writes an edition,
// which is delivered through its channels. Editions are made on a schedule
// or on demand (sendNow), and a changed seal of a delivered month makes a
// correction.
//
// Roles: everyone on the team reads; a member creates a DRAFT, which has no
// channel on and sends nothing; every other change is a maintainer's.
// These routes exist only while the server has deployments on;
// listRepositoryDeployments tells the two apart.

import type { ApiError } from "./errors.js";
import { getJSON, optionalJSON, query, request, sendJSON } from "./http.js";
import type { LiveDeployment, LiveToken, ViewerLink } from "./live.js";

const BASE = "/v1/repositories";

/** PeriodRule maps a due date to the report period an edition is of. */
export type PeriodRule =
  | "previous_month"
  | "previous_quarter"
  | "previous_year"
  | "same_month"
  | "same_quarter"
  | "same_year";

export const PERIOD_RULES: ReadonlyArray<{ value: PeriodRule; label: string }> = [
  { value: "previous_month", label: "Previous month" },
  { value: "previous_quarter", label: "Previous quarter" },
  { value: "previous_year", label: "Previous year" },
  { value: "same_month", label: "Same month" },
  { value: "same_quarter", label: "Same quarter" },
  { value: "same_year", label: "Same year" },
];

/** Channel is one way a deployment reaches its audience. */
export type Channel = "live" | "mail" | "webhook";

/**
 * Schedule says when an edition is due: a cron expression, or the day-th
 * workday of each month at a local time (HH:MM), both in the deployment's
 * time zone. Only the fields of its kind are set.
 */
export interface Schedule {
  kind: "cron" | "workday";
  cron?: string;
  day?: number;
  at?: string;
}

/** OpenWindow is a period that waits for the seals of its months. */
export interface OpenWindow {
  period: string;
  closesAt: string;
  /** The months (YYYY-MM) of the period without a seal yet. */
  missingMonths: string[];
}

/** LastRun is the last edition a schedule or a maintainer asked for. */
export interface LastRun {
  period: string;
  dueAt: string;
  /** queued, done, failed or late. */
  status: string;
  buildId?: string;
  buildStatus?: string;
  editionId?: string;
  editionStatus?: string;
  /** A fixed code: why it failed or is late. */
  error?: string;
}

/**
 * Deployment is a LiveDeployment of a repository. A legacy one is a live
 * artefact of a workspace that moved to its repository: it serves the
 * snapshot it was published from until a maintainer deploys it at a commit.
 */
export interface Deployment extends LiveDeployment {
  repositoryId: string;
  /** The commit it shows; empty for a legacy deployment. */
  commit?: string;
  legacy?: boolean;
  periodRule?: PeriodRule;
  schedule?: Schedule;
  timezone?: string;
  /** Absent for the built-in Monday to Friday calendar. */
  calendarId?: string;
  windowWorkdays?: number;
  channels?: Channel[];
  /** The mail audience; only a maintainer sees it. */
  audience?: string[];
  audienceCount?: number;
  mailLinkTtlDays?: number;
  nextDueAt?: string;
  /** Only a read of one deployment has it. */
  openWindow?: OpenWindow;
  /** Only a read of one deployment has it. */
  lastRun?: LastRun;
  /** The mail channel is on, but the server has no report mail. */
  mailUnavailable?: boolean;
}

/** DeploymentSettings are the fields a create or a change takes. */
export interface DeploymentSettings {
  periodRule?: PeriodRule;
  /** null removes the schedule: editions are then made on demand only. */
  schedule?: Schedule | null;
  timezone?: string;
  /** "" goes back to the built-in Monday to Friday calendar. */
  calendarId?: string;
  windowWorkdays?: number;
  audience?: string[];
  mailLinkTtlDays?: number;
}

/** CreateDraftRequest creates a draft at a commit, or at a branch's head. */
export interface CreateDraftRequest extends DeploymentSettings {
  artefact: string;
  /** A full commit id; omit to use the branch's head commit. */
  commit?: string;
  /** The branch whose head commit to use; the default branch when omitted. */
  branch?: string;
  visibility?: "team" | "token" | "public";
  token?: string;
  embedDomains?: string[];
  idleTimeoutSeconds?: number;
  sessionTtlSeconds?: number;
}

/** UpdateDeploymentRequest changes a deployment (maintainer+). */
export interface UpdateDeploymentRequest extends DeploymentSettings {
  /** Deploy at another commit; a legacy deployment moves to a commit here. */
  commit?: string;
  channels?: Channel[];
  visibility?: "team" | "token" | "public";
  token?: string;
  embedDomains?: string[];
  idleTimeoutSeconds?: number;
  sessionTtlSeconds?: number;
}

/** DiscoveredArtefact is one artefact a commit declares, with its deployments. */
export interface DiscoveredArtefact {
  artefact: string;
  file?: string;
  position?: number;
  deployments: Deployment[];
}

export interface Discovery {
  /** The commit that was indexed. */
  commit: string;
  items: DiscoveredArtefact[];
}

/**
 * EditionStatus: ready (built), delivered, blocked (its lint failed) or
 * withdrawn (its deployment was stopped before it was delivered).
 */
export type EditionStatus = "ready" | "delivered" | "blocked" | "withdrawn";

export interface EditionFile {
  name: string;
  size: number;
  sha256: string;
}

/** EditionSummary is an edition as the listing answers it: without its files, with their number. */
export interface EditionSummary {
  id: string;
  deploymentId: string;
  seq: number;
  status: EditionStatus;
  period: string;
  dataUntil?: string;
  /** schedule, seal (a correction), manual or api. */
  trigger?: string;
  actorId?: string;
  /** The edition this one corrects. */
  correctionOf?: string;
  buildId?: string;
  commit: string;
  sealSetDigest?: string;
  seals: { month: string; sealId: string }[];
  image?: string;
  imageDigest?: string;
  lockDigest?: string;
  params?: Record<string, string>;
  /** How many files it holds. */
  fileCount: number;
  outputsDigest?: string;
  bytes: number;
  deliveredAt?: string;
  correctionRequestedAt?: string;
  /** When retention removed the files of this blocked or withdrawn edition. */
  filesRemovedAt?: string;
  created: string;
}

/** Edition is one edition with its files. */
export interface Edition extends EditionSummary {
  files: EditionFile[];
}

export interface EditionPage {
  items: EditionSummary[];
  /** Pass as before for the next page; absent on the last one. */
  nextBefore?: number;
}

export interface Webhook {
  url: string;
  host: string;
  /** False once the breaker switched it off; a putWebhook switches it on. */
  enabled: boolean;
  disabledAt?: string;
  disabledReason?: string;
  /** Failed attempts in a row. */
  failures: number;
  failingSince?: string;
  /** Until when the secret before the last rotation still signs. */
  previousSecretUntil?: string;
}

export interface WebhookDelivery {
  id: string;
  /** The Bino-Delivery header; the same for every attempt of an event. */
  eventId: string;
  event: "edition.ready" | "edition.correction" | "deployment.late";
  editionId?: string;
  period: string;
  attempt: number;
  status: "pending" | "sending" | "succeeded" | "failed" | "skipped";
  dueAt: string;
  sentAt?: string;
  httpStatus?: number;
  durationMs?: number;
  bodySha256?: string;
  reason?: string;
}

export interface ReportMail {
  id: string;
  kind: "edition" | "correction" | "late" | "failed" | "budget_notice";
  recipient: string;
  status: "queued" | "sending" | "sent" | "failed";
  attempts: number;
  nextAttemptAt?: string;
  sentAt?: string;
  /** budget, mail_unavailable, rejected, transient, withdrawn or expired. */
  reason?: string;
  editionId?: string;
  created: string;
}

/**
 * listRepositoryDeployments returns the repository's deployments, oldest
 * first (viewer+), or null while the server has deployments off.
 */
export async function listRepositoryDeployments(id: string): Promise<Deployment[] | null> {
  const res = await optionalJSON<{ items: Deployment[] }>(
    `${BASE}/${encodeURIComponent(id)}/deployments`,
  );
  return res ? res.items : null;
}

/** createDeployment creates a draft (member+): no channel is on until a maintainer enables it. */
export function createDeployment(id: string, req: CreateDraftRequest): Promise<Deployment> {
  return sendJSON<Deployment>(`${BASE}/${encodeURIComponent(id)}/deployments`, "POST", req);
}

/**
 * discoverAt lists the live artefacts a commit declares (member+), or the
 * head commit of a branch when commit is empty. It runs the project indexer
 * in a sandbox, so call it only when the user asks for it.
 */
export function discoverAt(
  id: string,
  at: { commit?: string; branch?: string },
): Promise<Discovery> {
  return getJSON<Discovery>(`${BASE}/${encodeURIComponent(id)}/deployments/artefacts${query(at)}`);
}

/** getDeployment returns one deployment with its open window and last run (viewer+). */
export function getDeployment(id: string, depId: string): Promise<Deployment> {
  return getJSON<Deployment>(depPath(id, depId));
}

/** updateDeployment changes a deployment (maintainer+). */
export function updateDeployment(
  id: string,
  depId: string,
  req: UpdateDeploymentRequest,
): Promise<Deployment> {
  return sendJSON<Deployment>(depPath(id, depId), "PATCH", req);
}

/** deleteDeployment removes the deployment with its editions (maintainer+). */
export async function deleteDeployment(id: string, depId: string): Promise<void> {
  await request(depPath(id, depId), { method: "DELETE" });
}

/**
 * enable turns a draft on (maintainer+) with the channels named, or with
 * every channel it is set up for when none are named.
 */
export function enable(id: string, depId: string, channels?: Channel[]): Promise<Deployment> {
  return sendJSON<Deployment>(`${depPath(id, depId)}/enable`, "POST", channels ? { channels } : {});
}

/** stop takes the deployment out of service (maintainer+): no channel sends. */
export function stop(id: string, depId: string): Promise<Deployment> {
  return sendJSON<Deployment>(`${depPath(id, depId)}/stop`, "POST", {});
}

/** start puts a stopped deployment back in service (maintainer+). */
export function start(id: string, depId: string): Promise<Deployment> {
  return sendJSON<Deployment>(`${depPath(id, depId)}/start`, "POST", {});
}

/** mintTeamToken issues the caller's short-lived token for the live URL (viewer+). */
export function mintTeamToken(id: string, depId: string): Promise<LiveToken> {
  return sendJSON<LiveToken>(`${depPath(id, depId)}/token`, "POST", {});
}

/**
 * mintViewerLink signs one link for an end user of the customer's
 * (maintainer+). The params are signed into it and forced onto every
 * request. REPORT_PERIOD and BINO_DATA_DIR are the cluster's and refused.
 */
export function mintViewerLink(
  id: string,
  depId: string,
  req: { ttlSeconds?: number; params?: Record<string, string>; label?: string },
): Promise<ViewerLink> {
  return sendJSON<ViewerLink>(`${depPath(id, depId)}/links`, "POST", req);
}

/** rotateLinkKey invalidates every viewer link at once (maintainer+). */
export async function rotateLinkKey(id: string, depId: string): Promise<void> {
  await sendJSON(`${depPath(id, depId)}/link-key/rotate`, "POST", {});
}

/** rotateEditionKey invalidates the link of every report mail sent so far (maintainer+). */
export async function rotateEditionKey(id: string, depId: string): Promise<void> {
  await sendJSON(`${depPath(id, depId)}/edition-key/rotate`, "POST", {});
}

/**
 * sendNow queues the build of an edition now (maintainer+), for period or
 * for the one the period rule maps now to. It answers 409 period_not_sealed
 * while a month of the period has no seal.
 */
export function sendNow(
  id: string,
  depId: string,
  period?: string,
): Promise<{ buildId: string; period: string }> {
  return sendJSON(`${depPath(id, depId)}/send`, "POST", period ? { period } : {});
}

/** listEditions returns a page of editions, newest first (viewer+). */
export function listEditions(id: string, depId: string, before?: number): Promise<EditionPage> {
  return getJSON<EditionPage>(`${depPath(id, depId)}/editions${query({ before })}`);
}

/** getEdition returns one edition (viewer+). */
export function getEdition(id: string, depId: string, editionId: string): Promise<Edition> {
  return getJSON<Edition>(`${depPath(id, depId)}/editions/${encodeURIComponent(editionId)}`);
}

/**
 * editionFileURL is the route of one file of an edition. It needs the
 * Authorization header, so the UI downloads it with downloadEditionFile.
 */
export function editionFileURL(id: string, depId: string, editionId: string, name: string): string {
  const path = name.split("/").map(encodeURIComponent).join("/");
  return `${depPath(id, depId)}/editions/${encodeURIComponent(editionId)}/files/${path}`;
}

/** downloadEditionFile fetches one file of an edition as a blob (viewer+). */
export async function downloadEditionFile(
  id: string,
  depId: string,
  editionId: string,
  name: string,
): Promise<Blob> {
  const res = await request(editionFileURL(id, depId, editionId, name));
  return res.blob();
}

/**
 * rebuildEdition builds a delivered edition again (maintainer+) to show it
 * can be made again from what it records. It writes no edition; compare the
 * build's outputs digest with the edition's.
 */
export function rebuildEdition(
  id: string,
  depId: string,
  editionId: string,
): Promise<{ buildId: string }> {
  return sendJSON(
    `${depPath(id, depId)}/editions/${encodeURIComponent(editionId)}/rebuild`,
    "POST",
    {},
  );
}

/** getWebhook returns the webhook (maintainer+), or null when there is none. */
export async function getWebhook(id: string, depId: string): Promise<Webhook | null> {
  try {
    return await getJSON<Webhook>(`${depPath(id, depId)}/webhook`);
  } catch (e) {
    if ((e as ApiError).code === "webhook_not_found") return null;
    throw e;
  }
}

/**
 * putWebhook sets the webhook URL (maintainer+) and switches it on again.
 * The first one answers the signing secret, which is never shown again.
 */
export function putWebhook(
  id: string,
  depId: string,
  url: string,
): Promise<{ webhook: Webhook; secret?: string }> {
  return sendJSON(`${depPath(id, depId)}/webhook`, "PUT", { url });
}

/** deleteWebhook removes the webhook (maintainer+). */
export async function deleteWebhook(id: string, depId: string): Promise<void> {
  await request(`${depPath(id, depId)}/webhook`, { method: "DELETE" });
}

/**
 * rotateWebhookSecret draws a new signing secret (maintainer+). The old one
 * keeps signing, as a second signature, until previousSecretUntil.
 */
export function rotateWebhookSecret(
  id: string,
  depId: string,
): Promise<{ secret: string; previousSecretUntil: string }> {
  return sendJSON(`${depPath(id, depId)}/webhook/rotate`, "POST", {});
}

/** listWebhookDeliveries returns the newest webhook attempts, digests only (member+). */
export async function listWebhookDeliveries(id: string, depId: string): Promise<WebhookDelivery[]> {
  const { items } = await getJSON<{ items: WebhookDelivery[] }>(
    `${depPath(id, depId)}/webhook/deliveries`,
  );
  return items;
}

/** listMails returns the newest report mails and notices (maintainer+). */
export async function listMails(id: string, depId: string): Promise<ReportMail[]> {
  const { items } = await getJSON<{ items: ReportMail[] }>(`${depPath(id, depId)}/mails`);
  return items;
}

/** parseAudience splits a typed audience: one address per line, commas work too. */
export function parseAudience(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((a) => a.trim())
    .filter((a) => a !== "");
}

/** depPath builds one deployment's route base. */
function depPath(id: string, depId: string): string {
  return `${BASE}/${encodeURIComponent(id)}/deployments/${encodeURIComponent(depId)}`;
}
